import { useCallback, useEffect, useRef, useState } from "react";

// Any of these on the document counts as "someone is at the desk". Passive
// listeners so they never block scrolling; a tab coming back to the foreground
// also counts (the operator just switched back to look).
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "pointerdown",
  "pointermove",
  "keydown",
  "touchstart",
  "wheel",
  "scroll",
];

export type IdlePhase = "active" | "prompting" | "idle";

/**
 * Document-wide inactivity timer (ENG-1132 / ENG-2149). While `enabled`:
 *
 *   active ──(timeoutMs, no activity)──▶ prompting ──(graceMs)──▶ idle
 *
 * `prompting` is the "Still watching?" window: the owning view keeps the
 * stream up and shows the prompt. `idle` is the teardown. Both stay put until
 * `reset()` is called — activity alone never leaves them (a stray mouse move
 * shouldn't silently dismiss the prompt or re-mint a stream; the operator
 * clicks). With `graceMs` of 0 the timer goes straight to `idle`.
 *
 * Disabling clears everything, so callers gate `enabled` on "the stream is
 * actually playing" and the clock never runs during a cold start.
 *
 * `held` is explicit, ongoing engagement with the stream (a talk-down hold, a
 * listen-in hold or toggle). While held the clock is stopped; a hold that
 * starts while prompting dismisses the prompt exactly like the confirm button;
 * when the hold ends the full window starts again. It never leaves `idle` —
 * a torn-down stream still needs Resume.
 */
export const useIdleTimer = (
  enabled: boolean,
  timeoutMs: number,
  graceMs = 0,
  held = false
) => {
  const [phase, setPhase] = useState<IdlePhase>("active");
  const phaseRef = useRef<IdlePhase>("active");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heldRef = useRef(held);
  heldRef.current = held;

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const transition = (next: IdlePhase) => {
    phaseRef.current = next;
    setPhase(next);
  };

  const arm = useCallback(() => {
    clear();
    // Held: the clock stays stopped; the held effect re-arms on release.
    if (heldRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (graceMs <= 0) {
        transition("idle");
        return;
      }
      transition("prompting");
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        transition("idle");
      }, graceMs);
    }, timeoutMs);
  }, [timeoutMs, graceMs]);

  // Explicit confirm/resume: back to active and start counting again.
  const reset = useCallback(() => {
    transition("active");
    if (enabled) arm();
    else clear();
  }, [enabled, arm]);

  useEffect(() => {
    if (!enabled) {
      clear();
      transition("active");
      return;
    }
    arm();
    const onActivity = () => {
      // Once prompting or idle, only reset() (the button) moves on.
      if (phaseRef.current !== "active") return;
      arm();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") onActivity();
    };
    ACTIVITY_EVENTS.forEach((ev) =>
      document.addEventListener(ev, onActivity, { passive: true })
    );
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clear();
      ACTIVITY_EVENTS.forEach((ev) =>
        document.removeEventListener(ev, onActivity)
      );
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, arm]);

  useEffect(() => {
    if (!enabled) return;
    if (held) {
      clear();
      if (phaseRef.current === "prompting") transition("active");
    } else if (phaseRef.current === "active") {
      arm();
    }
  }, [held, enabled, arm]);

  return {
    idle: phase === "idle",
    prompting: phase === "prompting",
    reset,
  };
};
